import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { usePunditUserContext } from "@/react/contexts";
import type { PlanTiers } from "@/react/helpers/planHelpers";
import {
  getNextTier,
  isPlanEnterprise,
  isPlanProfessional,
} from "@/react/helpers/planHelpers";
import { useScheduleDowngradesApi } from "@/react/hooks/useScheduleDowngradesApi";
import { supportedCurrencies } from "@circle-react/config/Currencies";
import { planIntervals } from "@circle-react/helpers/plansHelpers";
import type { PlanIntervals } from "@circle-react/helpers/plansHelpers";
import { useAccountSubscriptionApi } from "@circle-react/hooks/useAccountSubscriptionApi";
import { usePlanStatus } from "@circle-react/hooks/usePlanStatus";
import type { Plan } from "@circle-react/types";
import type { CurrentPlan } from "@circle-react/types";
import { useCurrentPlanApi } from "../hooks/useCurrentPlanApi";
import { useFetchPlans } from "../hooks/usePlansApi";

interface PlanContextProps {
  billingCurrency: string | null;
  billingCycle: PlanIntervals | null;
  currentPlan?: CurrentPlan;
  isCurrentPlanLoading: boolean;
  isListingPlans: boolean;
  isPlansLoading: boolean;
  isRenewLoading: boolean;
  isSchedulingDowngrade: boolean;
  isUpdatingSubscription: boolean;
  plans: Plan[];
  refetchCurrentPlan: any;
  refetchPlans: any;
  renewSubscription: any;
  scheduleDowngrade: ReturnType<
    typeof useScheduleDowngradesApi
  >["scheduleDowngrade"];
  selectedPlan?: Plan;
  selectedPlanTier?: PlanTiers;
  setSelectedPlanTier: (planTier: PlanTiers) => void;
  shouldRenderBillingCurrency: boolean;
  updateSubscription: any;
}

const PlansContext = createContext<PlanContextProps | undefined>(undefined);
PlansContext.displayName = "PlansContext";

export const usePlans = (): PlanContextProps => {
  const context = useContext(PlansContext);
  if (!context) {
    throw new Error("usePlans must be used within a PlansProvider");
  }
  return context;
};

interface PlansProviderProps {
  children: ReactNode;
}

export const PlansProvider = ({ children }: PlansProviderProps) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryBillingCycle = queryParams.get("billing_cycle");
  const queryBillingCurrency = queryParams.get("billing_currency");
  const queryLocale = queryParams.get("locale");

  const { currentPlan, isCurrentPlanLoading, refetchCurrentPlan } =
    useCurrentPlanApi();
  const {
    isCommunityOnPaidPlan,
    isCommunityOnTrial,
    isExpiredTrial,
    canChooseCurrency,
  } = usePlanStatus();

  const {
    experimentation: { experiments, flags },
  } = usePunditUserContext();

  const getDefaultBillingCycle = useCallback(() => {
    if (isCommunityOnTrial || isExpiredTrial) {
      return planIntervals.year;
    }

    if (!isCurrentPlanLoading && currentPlan) {
      return currentPlan.interval;
    }

    return planIntervals.year;
  }, [isCommunityOnTrial, isCurrentPlanLoading, currentPlan, isExpiredTrial]);

  const [billingCycle, setBillingCycle] = useState<PlanIntervals | null>(null);
  const [locale, setLocale] = useState<string | null>(null);
  const [billingCurrency, setBillingCurrency] = useState<string | null>(null);

  const {
    renewSubscription,
    isRenewLoading,
    updateSubscription,
    isUpdatingSubscription,
  } = useAccountSubscriptionApi();

  const { scheduleDowngrade, isSchedulingDowngrade } =
    useScheduleDowngradesApi();

  const { plans, isPlansLoading, refetchPlans } = useFetchPlans({
    billing_cycle: billingCycle,
    billing_currency: billingCurrency,
    locale: locale,
  });

  useEffect(() => {
    if (queryBillingCurrency) {
      setBillingCurrency(queryBillingCurrency);
    } else if (currentPlan) {
      setBillingCurrency(null);
    }

    if (queryLocale) {
      setLocale(queryLocale);
    }

    if (
      queryBillingCycle &&
      (queryBillingCycle === planIntervals.month ||
        queryBillingCycle === planIntervals.year)
    ) {
      setBillingCycle(queryBillingCycle);
    } else if (currentPlan) {
      setBillingCycle(getDefaultBillingCycle());
    }
  }, [location, getDefaultBillingCycle, currentPlan]);

  const isListingPlans = isPlansLoading || isCurrentPlanLoading;

  const hasLoadedPlans = plans.length > 0 && !isPlansLoading;
  const firstPlanCurrency = hasLoadedPlans ? plans[0].currency : null;

  const shouldRenderBillingCurrency =
    canChooseCurrency &&
    (!!queryBillingCurrency ||
      firstPlanCurrency === supportedCurrencies.BRL.code.toLowerCase());

  const billingCurrencyToDisplay = firstPlanCurrency || billingCurrency;

  const [selectedPlanTier, setSelectedPlanTier] = useState<
    PlanTiers | undefined
  >(undefined);

  useEffect(() => {
    if (!plans.length) return;
    if (selectedPlanTier) return;

    if (isCommunityOnTrial || isExpiredTrial) {
      const defaultPlan = plans.find(plan => isPlanProfessional(plan.tier));
      defaultPlan && setSelectedPlanTier(defaultPlan.tier);
    } else if (
      isCommunityOnPaidPlan &&
      currentPlan &&
      !isPlanEnterprise(currentPlan.tier)
    ) {
      const defaultPlan = plans.find(
        plan => plan.tier === getNextTier(currentPlan.tier),
      );
      defaultPlan && setSelectedPlanTier(defaultPlan.tier);
    }
  }, [
    currentPlan,
    isCommunityOnPaidPlan,
    isCommunityOnTrial,
    isExpiredTrial,
    plans,
    selectedPlanTier,
  ]);

  const selectedPlan = plans.find(plan => plan.tier === selectedPlanTier);

  // [Professional Price Test][After Release] Remove this hack to show the right discount
  // After professional price increased for control.
  const isProfessionalPriceTestActive = Boolean(
    experiments?.some(
      experiment => experiment.flag_name === "professional_price_test",
    ),
  );
  const isProfessionalPriceTestControl =
    typeof flags?.professional_price_test === "object" &&
    flags?.professional_price_test?.type === "control";

  const shouldOverrideProfessionalPrice =
    isProfessionalPriceTestActive &&
    isProfessionalPriceTestControl &&
    billingCycle === planIntervals.year;

  const exposedPlans = shouldOverrideProfessionalPrice
    ? plans.map(plan => ({
        ...plan,
        annual_discount_amount_with_currency:
          plan.tier === "professional"
            ? "$120"
            : plan.annual_discount_amount_with_currency,
        fetch_discount:
          plan.tier === "professional" ? "-10%" : plan.fetch_discount,
      }))
    : plans;

  return (
    <PlansContext.Provider
      value={{
        plans: exposedPlans,
        isPlansLoading,
        selectedPlanTier,
        selectedPlan,
        currentPlan,
        isCurrentPlanLoading,
        billingCycle,
        billingCurrency: billingCurrencyToDisplay,
        renewSubscription,
        isRenewLoading,
        shouldRenderBillingCurrency,
        updateSubscription,
        isUpdatingSubscription,
        refetchPlans,
        refetchCurrentPlan,
        isListingPlans,
        setSelectedPlanTier,
        scheduleDowngrade,
        isSchedulingDowngrade,
      }}
    >
      {children}
    </PlansContext.Provider>
  );
};
